import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LiveDemosPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState({ type: '', message: '' });

  const handleNotifyMe = (e) => {
    e.preventDefault();
    // For now, just show success message
    // Later can be connected to actual email collection
    setNotifyStatus({
      type: 'success',
      message: 'Thank you! We\'ll notify you when demos are live.'
    });
    setEmail('');
    
    setTimeout(() => {
      setNotifyStatus({ type: '', message: '' });
    }, 5000);
  };

  const demos = [
    {
      title: 'AI Model Stress Testing Dashboard',
      category: 'AI Reliability Testing',
      description: 'Interactive platform to validate AI model performance, consistency, and reliability under extreme conditions. Test your models against adversarial scenarios before production deployment.',
      features: [
        'Real-time performance analysis across 1000+ test scenarios',
        'Adversarial attack simulation & defense validation',
        'Consistency scoring with statistical confidence intervals',
        'Automated test suite generation using ML',
        'Compliance report generation (ISO 42001, EU AI Act)'
      ],
      comingSoon: true
    },
    {
      title: 'SimNet™ Network Visualizer',
      category: 'Infrastructure Simulation',
      description: 'Visualize and simulate water, gas, sewage, and district heating networks with real-time CFD analysis. Interactive 3D network topology with live optimization recommendations.',
      features: [
        'Interactive 3D network topology mapping',
        'Real-time hydraulic flow and pressure visualization',
        'AI-powered leak detection simulation',
        'What-if scenario analysis (pipe breaks, demand surges)',
        'Infrastructure optimization dashboards'
      ],
      comingSoon: true
    },
    {
      title: 'Procurement Intelligence Analyzer',
      category: 'Supply Chain Optimization',
      description: 'AI-powered tool to analyze procurement data, identify hidden cost-saving opportunities, and assess supplier risk exposure. Upload your spend data and receive instant insights.',
      features: [
        'Instant procurement data analysis (CSV/Excel upload)',
        'Cost-saving opportunity detection (15-30% savings)',
        'Supplier risk assessment & concentration analysis',
        'Spend pattern visualization & anomaly detection',
        'Automated RFP recommendation engine'
      ],
      comingSoon: true
    },
    {
      title: 'Project Execution Simulator',
      category: 'Project Management',
      description: 'Simulate project execution scenarios with AI-powered risk prediction. Test different resource allocations and see predicted outcomes before committing to decisions.',
      features: [
        'Monte Carlo simulation for deadline prediction',
        'Resource allocation optimization scenarios',
        'Risk probability heat maps',
        'Team workload balancing simulator',
        'Budget impact analysis across scenarios'
      ],
      comingSoon: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHx8MTc2MTA0Njc0OHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            Experience <span className="text-[#00E600]">NYAXO AI</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed mb-8">
            Interactive demonstrations and detailed walkthroughs showcasing our AI-powered solutions. 
            See how intelligent systems transform operations before implementation.
          </p>
          <div className="inline-block bg-[#1A1A1A] border border-[#00E600] rounded-lg px-6 py-3">
            <p className="text-[#00E600] font-semibold uppercase tracking-wide text-sm">
              Live Demos Coming Soon
            </p>
          </div>
        </div>
      </section>

      {/* Current Documentation Available */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 uppercase tracking-wide">
              Available <span className="text-[#00E600]">Now</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              While interactive demos are in development, explore detailed documentation, technical specifications, 
              and video walkthroughs of our AI solutions on our service pages.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'AI Stress Testing', link: '/services/ai-stress-testing', available: 'Detailed technical documentation' },
              { name: 'Procurement Intelligence', link: '/services/procurement-intelligence', available: 'Case studies & ROI calculators' },
              { name: 'Utility Network Intelligence', link: '/services/water-gas-management', available: 'SimNet™ software specifications' },
              { name: 'Project Management SaaS', link: '/services/project-management', available: 'Platform capabilities overview' },
              { name: 'Digital Transformation', link: '/services/digital-transformation', available: 'Transformation frameworks' }
            ].map((service, idx) => (
              <div
                key={idx}
                onClick={() => navigate(service.link)}
                className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 cursor-pointer group"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2 uppercase tracking-wide">{service.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-3">{service.available}</p>
                <div className="flex items-center text-[#00E600] text-sm font-semibold group-hover:translate-x-1 transition-transform duration-300">
                  View Details
                  <svg className="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Cards */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 uppercase tracking-wide">
              Interactive Demos <span className="text-[#00E600]">In Development</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              We're building hands-on, interactive demonstrations so you can experience our AI capabilities 
              firsthand. Test scenarios, upload your own data, and see results in real-time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {demos.map((demo, index) => (
              <div
                key={index}
                data-testid={`demo-card-${index}`}
                className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-8 hover:border-[#00E600] transition-all duration-300 relative overflow-hidden group"
              >
                {/* Coming Soon Badge */}
                {demo.comingSoon && (
                  <div className="absolute top-4 right-4 bg-[#00E600] text-[#0D0D0D] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                    Coming Soon
                  </div>
                )}

                {/* Category */}
                <div className="text-[#00E600] text-sm font-semibold uppercase tracking-wide mb-2">
                  {demo.category}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-wide">
                  {demo.title}
                </h3>

                {/* Description */}
                <p className="text-[#B3B3B3] leading-relaxed mb-6">
                  {demo.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  <p className="text-white font-semibold text-sm uppercase tracking-wide mb-3">Key Features:</p>
                  {demo.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-2 text-sm text-[#B3B3B3]">
                      <svg className="w-4 h-4 text-[#00E600] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Button */}
                <button
                  disabled
                  className="w-full bg-[#1A1A1A] border border-[#2F2F2F] text-[#B3B3B3] font-semibold py-3 rounded-md uppercase tracking-wide text-sm cursor-not-allowed opacity-60"
                >
                  Demo In Development
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Notify Me Section */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Get Notified When Demos Launch</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Be among the first to experience our interactive demonstrations. We'll send you an email when they're ready.
          </p>

          {/* Notification Form */}
          <form onSubmit={handleNotifyMe} className="max-w-md mx-auto">
            {notifyStatus.message && (
              <div className="mb-4 p-3 rounded-md bg-[#00E600]/10 border border-[#00E600]">
                <p className="text-[#00E600] text-sm font-semibold">{notifyStatus.message}</p>
              </div>
            )}
            
            <div className="flex gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your.email@company.com"
                className="flex-1 bg-[#1A1A1A] border border-[#2F2F2F] rounded-md px-4 py-3 text-white focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_20px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm whitespace-nowrap"
              >
                Notify Me
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Custom Solutions CTA */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Need Solutions Now?</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            While interactive demos are in development, our team is ready to deploy custom AI solutions for your business. 
            Schedule a consultation to discuss your specific challenges and see how NYAXO can help.
          </p>
          <button
            onClick={() => navigate('/#contact')}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Schedule Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default LiveDemosPage;
